import { ImprovedPolicyGenerationReasonCode } from 'src/types/workflowBuilderAPITypes';

/**
 * Poll cadence for the latest-job row while a run is queued or running. The
 * triggered→running transition is not pushed (Pusher fires on terminal states
 * only), so this poll is what flips "Queued" to "Running"; runs take ~1h.
 */
export const GENERATION_JOB_POLL_INTERVAL_MS = 60_000;

/** Post-click trigger lock, long enough to cover the 202 round trip. */
export const GENERATE_CLICK_LOCK_MS = 5_000;

/** Admin guidance limits, mirrored by the generation API. */
export const TEXT_CONTEXT_CHAR_LIMIT = 2_000;
export const FILE_CONTEXT_CHAR_LIMIT = 6_000;
export const COMBINED_CONTEXT_CHAR_LIMIT = 8_000;

/**
 * Product-decided traffic floor: the card withholds Generate until the
 * workflow has handled this many conversations in the last 30 days, rendered
 * as its own meter. FE-only by design — the BE's eligibility check doesn't
 * know about it (the weekly cron and a direct POST are unaffected), which is
 * safe because the FE is only ever stricter than the BE.
 */
export const MIN_CONVERSATIONS_TO_GENERATE = 50;

/**
 * A submitted review classifies asynchronously (~1 min), so a single
 * refetch-on-focus can still read the old counts. For a window after the tab
 * regains focus (or the card mounts), poll eligibility so the signal meters
 * visibly move while the user watches; outside the window the existing
 * focus/mount refetches take over.
 */
export const ELIGIBILITY_POLL_WINDOW_MS = 90_000;
export const ELIGIBILITY_POLL_INTERVAL_MS = 15_000;

/**
 * Cap on the tracker's seen-(run_id, state) de-dup set. Terminal events are
 * rare (one run per hour-ish), so 50 covers any realistic builder session;
 * the cap just keeps a day-long tab with a flaky Pusher connection from
 * growing the set unboundedly. Oldest entries evict first.
 */
export const MAX_SEEN_GENERATION_EVENTS = 50;

/**
 * Render order for blocking reasons, roughly the order a customer can act
 * on them. Codes the FE doesn't know sort after everything.
 */
export const REASON_RENDER_RANK = {
  empty_policy: 1,
  insufficient_data: 5,
  job_in_flight: 3,
  no_published_version: 0,
  pending_recommendation_exists: 2,
  purpose_mismatch: 4,
} as const satisfies Record<ImprovedPolicyGenerationReasonCode, number>;
